import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import {
  TWO_FA_CODE_LENGTH,
  TWO_FA_CODE_RESEND_TIMEOUT,
  TWO_FA_CODE_RETRY_TIMEOUT,
} from 'shared/constants/auth'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import {
  type AuthStore,
  type IResponse2FAOwnerEmailSettings,
  type IResponse2FASettings,
} from 'entities/Auth'
import { AuthApi } from 'entities/Auth/api'
import { getOrCreateDeviceId } from 'entities/Auth/lib/getOrCreateDeviceId.ts'
import {
  type ITwoFaMethod,
  type ITwoFaMethodType,
  type ITwoFaResendType,
  type ITwoFaStep,
} from './type'
import { TwoFaMethodModalContent } from '../ui/Method/TwoFaMethodModalContent'
import { TwoFaMethodModalActions } from '../ui/Method/TwoFaMethodModalActions'
import { TwoFaCodeModalContent } from '../ui/Code/TwoFaCodeModalContent'
import { TwoFaCodeModalActions } from '../ui/Code/TwoFaCodeModalActions'

export class TwoFaModalStore {
  private _loading = false
  private _twoFaSettings: IResponse2FASettings | null = null
  private _ownerEmailSettings: IResponse2FAOwnerEmailSettings | null = null
  private _twoFaMethod: ITwoFaMethod | null = null
  private _twoFaStep: ITwoFaStep = 'method'
  private _twoFaModalId = ''
  private _twoFaCode = ''
  private _twoFaCodeError = ''
  private _twoFaCodeAlertError = ''
  private _resendTimer = 0
  private _resendType: ITwoFaResendType = 'resend'
  private _resendInterval: NodeJS.Timer | null = null
  private _remember = false
  private _isChangeSecurityMode = false
  private _qrCodeUrl = ''
  private _onSuccess: (() => void) | null = null

  constructor(private _authStore: AuthStore) {
    makeAutoObservable(this)
  }

  get hasResendTimer() {
    return !!this._resendTimer
  }

  get resendTimer() {
    return this._resendTimer
  }

  get remember() {
    return this._remember
  }

  get resendText() {
    const min = Math.floor(this.resendTimer / 60)
    const sec = this.resendTimer % 60
    const time = `${min}:${sec > 9 ? sec : `0${sec}`}`

    if (this._resendType === 'retry') return `Try again in ${time}`
    return `Resend again in ${time}`
  }

  get twoFaMethod() {
    return this._twoFaMethod
  }

  get shouldSendTwoFaCode() {
    return (
      this.twoFaMethod?.type === 'email' ||
      this.twoFaMethod?.type === 'sms' ||
      this.twoFaMethod?.type === 'owner_email'
    )
  }

  get isValidTwoFaCode() {
    return this._twoFaCode.replace(/[^-.0-9]/g, '').length === TWO_FA_CODE_LENGTH
  }

  get twoFaStep() {
    return this._twoFaStep
  }

  get twoFaCode() {
    return this._twoFaCode
  }

  get twoFaCodeError() {
    return this._twoFaCodeError
  }

  get twoFaCodeAlertError() {
    return this._twoFaCodeAlertError
  }

  get twoFaSettings(): ITwoFaMethod[] | undefined {
    return this._twoFaSettings
      ?.filter((item) => (this._isChangeSecurityMode ? true : item.value))
      .map((item) => {
        if (item.type === 'app') {
          return {
            ...item,
            title: 'Authenticator app',
            desc: 'Receive a one-time passcode via an authenticator app',
            icon: 'qr',
          }
        }

        if (item.type === 'email') {
          return {
            ...item,
            title: 'Email',
            desc: 'Receive a one-time passcode via email',
            icon: 'email',
          }
        }

        if (item.type === 'sms') {
          return {
            ...item,
            title: 'SMS',
            desc: 'Receive a one-time passcode via SMS',
            icon: 'testSMS',
          }
        }

        return item
      })
  }

  get twoFaLabel() {
    if (!this.twoFaMethod) return ''
    switch (this.twoFaMethod.type) {
      case 'app': {
        return 'We’ve sent it to the authenticator app'
      }
      default:
        return `We’ve sent it to ${this.twoFaMethod.label}`
    }
  }

  get loading() {
    return this._loading
  }

  get twoFaModalId() {
    return this._twoFaModalId
  }

  get disabledCodeResend() {
    return Boolean(this.twoFaCodeAlertError && this.hasResendTimer)
  }

  get isSingleMethod() {
    return this.twoFaSettings?.length === 1
  }

  get deviceId() {
    return localStorage.getItem('device_id')
  }

  get isChangeSecurityMode() {
    return this._isChangeSecurityMode
  }

  get qrCodeUrl() {
    return this._qrCodeUrl
  }

  resetTwoCodeModal = () => {
    this._twoFaCode = ''
    this._twoFaCodeError = ''
    this._twoFaCodeAlertError = ''
    this._resendTimer = 0
    this._resendType = 'resend'
    this._remember = false
    URL.revokeObjectURL(this._qrCodeUrl)
    this._qrCodeUrl = ''
  }

  handleResendTimeout = (type: ITwoFaResendType) => {
    this._resendTimer = type === 'retry' ? TWO_FA_CODE_RETRY_TIMEOUT : TWO_FA_CODE_RESEND_TIMEOUT
    this._resendType = type

    if (this._resendInterval) clearInterval(this._resendInterval)

    this._resendInterval = setInterval(() => {
      if (this.resendTimer === 0 && this._resendInterval) {
        clearInterval(this._resendInterval)
        this._twoFaCodeAlertError = ''
        return
      }

      this._resendTimer = this.resendTimer - 1
    }, 1000)
  }

  setTwoFaCode = (value: string) => {
    this.clearTwoFaCodeError()
    this._twoFaCode = value
  }

  resendTwoFaCode = () => {
    if (!this.twoFaMethod || !this.shouldSendTwoFaCode || this.hasResendTimer) return
    this.sendTwoFaCode()
    this.handleResendTimeout('resend')
  }

  toggleRemember = () => {
    this._remember = !this._remember
  }

  sendTwoFaCode = async () => {
    if (!this.shouldSendTwoFaCode || this.hasResendTimer) return true
    if (!this.twoFaMethod) return false
    this.clearTwoFaCodeError()
    try {
      this.setLoading(true)
      const prevTries = this._methodTriesMap.get(this.twoFaMethod.type) || 0
      this._methodTriesMap.set(this.twoFaMethod.type, prevTries + 1)
      const params = {
        type: this.twoFaMethod.type as 'sms' | 'email',
      }

      await this._authStore.sendTwoFaCode(params, this._isChangeSecurityMode)
      return true
    } catch (e) {
      if (e instanceof AxiosError) {
        showToast({ type: 'error', title: e.errors.getError('message') || 'Error' })
      }
      logger.error(e)
      return false
    } finally {
      this.setLoading(false)
    }
  }

  clearTwoFaCodeError = () => {
    if (!!this.twoFaCodeError) this._twoFaCodeError = ''
    if (!!this.twoFaCodeAlertError) this._twoFaCodeAlertError = ''
  }

  onVerifyCode = async () => {
    if (!this.twoFaMethod || !this.isValidTwoFaCode) return
    this.clearTwoFaCodeError()
    const deviceId = getOrCreateDeviceId()

    try {
      this.setLoading(true)
      const params = {
        type: this.twoFaMethod.type,
        code: this.twoFaCode,
        remember: this.remember,
        device_id: deviceId,
        value: this.twoFaMethod.value,
      }
      await this._authStore.verifyTwoFaCode(params, this._isChangeSecurityMode)
      modalStore.removeModal(this._twoFaModalId)
      this._onSuccess?.()
      this._onSuccess = null
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.response?.data?.status === 429) {
          this._twoFaCodeAlertError = e.errors.getError('message')
          this.handleResendTimeout('retry')
        } else {
          this._twoFaCodeError = e.errors.getError('message')
        }
      }
      logger.error(e)
    } finally {
      this.setLoading(false)
    }
  }

  setTwoFaStep = (value: ITwoFaStep) => {
    this._twoFaStep = value
  }

  setTwoFaMethod = (value: ITwoFaMethod | null) => {
    this._twoFaMethod = value
  }

  openTwoFaModal = ({
    settings,
    onSuccess,
    owner_email_settings,
  }: {
    settings: IResponse2FASettings
    owner_email_settings?: IResponse2FAOwnerEmailSettings
    onSuccess?: () => void
  }) => {
    this._onSuccess = onSuccess || null
    this._twoFaSettings = settings
    this._ownerEmailSettings = owner_email_settings || null

    if (this.twoFaSettings && (this.isSingleMethod || !this.twoFaMethod)) {
      this.setTwoFaMethod(this.twoFaSettings[0])
    }
    if (this.twoFaMethod && (this.isSingleMethod || this.twoFaStep === 'code')) {
      this.sendTwoFaCode()
      this.openCodeModal()
      return
    }
    this.openMethodModal()
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  openMethodModal = async () => {
    modalStore.removeModal(this.twoFaModalId)
    this.setTwoFaStep('method')
    this._twoFaModalId = nanoid()
    modalStore.addModal({
      id: this.twoFaModalId,
      type: ModalTypeList.DEFAULT,
      title: 'Verification method',
      ModalContentProps: {
        store: this,
      },
      ModalContent: TwoFaMethodModalContent,
      ModalActions: TwoFaMethodModalActions,
      paddingContent: '0px 24px 16px 24px',
      width: 540,
    })
  }

  openCodeModal = async () => {
    this.setTwoFaStep('code')
    modalStore.removeModal(this.twoFaModalId)
    this._twoFaModalId = nanoid()

    modalStore.addModal({
      id: this.twoFaModalId,
      type: ModalTypeList.DEFAULT,
      title: this._isChangeSecurityMode ? 'Verify account' : 'Verification required',
      ModalContentProps: {
        store: this,
      },
      ModalContent: TwoFaCodeModalContent,
      ModalActions: TwoFaCodeModalActions,
      paddingContent: '0',
      onClose: () => {
        modalStore.removeModal(this.twoFaModalId)
      },
      width: 360,
    })
  }

  setChangeSecurityMode(value: boolean) {
    this._isChangeSecurityMode = value
  }

  getAppQrCode = async () => {
    try {
      const { data } = await AuthApi.getTwoFAuthQrCode()

      runInAction(() => {
        const blob = new Blob([data.qr_code], { type: 'image/svg+xml' })

        this._qrCodeUrl = URL.createObjectURL(blob)
      })
    } catch (e) {
      logger.error(e)
    }
  }

  private _methodTriesMap: Map<ITwoFaMethodType, number> = new Map()
  get isShowVerifyViaOwnerEmail() {
    if (!this._twoFaMethod?.type) return false
    if (!this._ownerEmailSettings?.value) return false
    if (this._twoFaMethod?.type === 'owner_email') return false
    if (!!this._resendTimer) return false

    const tries = this._methodTriesMap.get(this._twoFaMethod.type)

    return !!tries && tries > 1
  }

  verifyViaOwnerEmail = () => {
    this.setTwoFaMethod({
      type: 'owner_email',
      value: true,
      label: this._ownerEmailSettings?.label || '',
      disabled: false,
    })
    this.sendTwoFaCode()
    this.handleResendTimeout('resend')
  }
}
